import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useOptionalAuth } from '@/components/AuthProvider';
import { canCreateWorkspace } from '@/lib/workspaces/manageWorkspaces';

/**
 * The single loading state for Manage workspaces (`/home/workspaces`).
 *
 * Opening it used to show a bare route spinner (the `<Suspense>` fallback), a
 * blank `RoleRoute` frame, then a "Loading workspaces…" line inside the table,
 * and each row's member count read "—" until the roster arrived. The route and
 * the page now draw this: the real heading (it is static copy) above the
 * table, whose rows are placeholders until the workspace list lands.
 */

const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 border-b border-[#EAECF0] px-4 py-3.5 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_140px_140px_44px] sm:gap-3 sm:px-5';

/** Placeholder rows — mirror the workspace row: avatar, name, role, team, actions. */
export const ManageWorkspacesRowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div aria-busy="true" data-testid="manage-workspaces-rows-skeleton">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className={ROW_GRID}>
        <div className="flex min-w-0 items-center gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 rounded" style={{ width: `${40 + ((i * 17) % 25)}%` }} />
            <Skeleton className="h-3 w-28 rounded" />
          </div>
        </div>
        <div className="col-span-2 flex items-center gap-3 pl-12 sm:contents">
          <div>
            <Skeleton className="h-6 w-16 rounded-md" />
          </div>
          <div>
            <Skeleton className="h-4 w-20 rounded" />
          </div>
        </div>
        <div className="col-start-2 row-start-1 flex justify-end sm:col-start-auto sm:row-start-auto">
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
    ))}
  </div>
);

/** A member count still loading — same footprint as "3 members". */
export const MemberCountSkeleton: React.FC = () => (
  <Skeleton className="h-4 w-20 rounded" data-testid="manage-workspace-team-count-skeleton" />
);

/** Page-level placeholder for the route guard and the `<Suspense>` fallback. */
export const ManageWorkspacesPageSkeleton: React.FC = () => {
  const auth = useOptionalAuth();
  const showCreate = canCreateWorkspace(auth?.user ?? null);
  return (
    <div
      className="mx-auto w-full max-w-5xl px-1 py-2"
      aria-busy="true"
      aria-label="Loading workspaces"
      data-testid="manage-workspaces-page-skeleton"
    >
      <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5 sm:flex-wrap sm:items-start">
        <div className="min-w-0">
          <h1 className="text-[20px] font-bold leading-7 text-[#101828] sm:text-[28px] sm:leading-8">Manage workspaces</h1>
          <p className="mt-0.5 text-[12.5px] leading-[18px] text-[#667085] sm:mt-1 sm:text-[14px] sm:leading-normal">
            Every business you belong to — role, team size, and actions.
          </p>
        </div>
        {showCreate ? <Skeleton className="h-8 w-[72px] shrink-0 rounded-lg sm:h-9 sm:w-[136px]" /> : null}
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white">
        <div className="hidden grid-cols-[minmax(0,1fr)_140px_140px_44px] gap-3 border-b sm:grid border-[#EAECF0] px-5 py-3 text-[12px] font-medium text-[#98A2B3]">
          <div>Workspace</div>
          <div>Your role</div>
          <div>Team members</div>
          <div />
        </div>
        <ManageWorkspacesRowsSkeleton />
      </div>
    </div>
  );
};

export default ManageWorkspacesPageSkeleton;
